import React, { useEffect, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { message, Button, Form, Table } from 'antd';
import {TextArea} from 'components/Form';
import useQueryApiClient from 'utils/useApiClient';
import '../styles.scss';
import moment from 'moment';
import { config } from 'config/config';


interface AssessmentOverviewInterface {
    activityFormData: any;
    submission: any;
    loadAssignment: Function;
    setIsSubmitLoading: React.Dispatch<React.SetStateAction<boolean>>;
    isSubmitLoading: boolean;
    getStatusTag: Function;
}

const AssessmentOverview: React.FC<AssessmentOverviewInterface> = ({
    activityFormData,
    submission,
    loadAssignment,
    setIsSubmitLoading,
    isSubmitLoading,
    getStatusTag
}) => {
    const [overviewRows, setOverviewRows] = useState<any[]>([]);
    const [selectedInstructor, setSelectedInstructor] = useState<any>(null);

    const intl = useIntl();
    const [form] = Form.useForm();

    const parseInitiateData = (initiateData: any) => ({
        status: initiateData.isInitiated,
        title: initiateData.isInitiated ? intl.formatMessage({id: 'assessment.assessment_initiated'}) : intl.formatMessage({id: 'assessment.waiting_initiate'}),
        tagline: initiateData.initiatedDate,
        initiator: initiateData.initiatedByUser && `${initiateData.initiatedByUser.name} ${initiateData.initiatedByUser.surname} `,
        comments: null,
        actions: initiateData.canInitiate && {
            message: intl.formatMessage({id: 'assessment.initiate_assessment'}),
            onClick: () => {
                setIsSubmitLoading(true)
                initiateAssessment()
            }
        }
    })

    const parseInstructorData = (instructorData: any) => ({
        status: instructorData.isSubmitted,
        title: instructorData.instructorUser && `${instructorData.instructorUser.name} ${instructorData.instructorUser.surname}`,
        tagline: instructorData.submittedDate,
        initiator: null,
        comments: instructorData.comments,
        actions: instructorData.canSubmit && !instructorData.isSubmitted && {
            message: intl.formatMessage({id: 'assessment.submit_comment'}),
            onClick: () => setSelectedInstructor(instructorData)
        }
    })

    const parseSubmitData = (finalAssessment: any) => ({
        status: finalAssessment.isSubmitted,
        title: finalAssessment.isSubmitted ? intl.formatMessage({id: 'assessment.final_assessment'}) : intl.formatMessage({id: 'courses.topic_status.final_assessment'}),
        tagline: finalAssessment.gradedDate,
        gradeDate: finalAssessment.gradedDate ?? null,
        initiator: finalAssessment.gradedByUser && `${finalAssessment.gradedByUser.name} ${finalAssessment.gradedByUser.surname} `,
        comments: finalAssessment.comments ?? null,
        actions: null,
    })

    const parseAcknowledgementData = (acknowledgement: any) => ({
        status: !!acknowledgement.acknowledgedAt,
        isPending: !acknowledgement.acknowledgedAt,
        title: acknowledgement.acknowledgedAt
            ? intl.formatMessage({id: 'assessment.acknowledged'})
            : intl.formatMessage({id: 'assessment.not_acknowledged'}),
        tagline: acknowledgement.acknowledgedAt,
        gradeDate: null,
        initiator: null,
        comments: null,
        actions: null,
    })

    const buildUserMap = (users: any[]): Record<number, any> =>
        (users ?? []).reduce((map: Record<number, any>, u: any) => {
            map[u.user_type_id] = u;
            return map;
        }, {});

    const formatUserName = (userMap: Record<number, any>, typeId: number | null): string | null => {
        if (!typeId) return null;
        const u = userMap[typeId];
        return u ? `${u.name} ${u.surname}` : null;
    };

    const parseGraduationFromHistory = (entry: any, userMap: Record<number, any>) => ({
        status: true,
        isAnnulled: false,
        title: intl.formatMessage({ id: 'assessment.final_assessment' }),
        tagline: entry.grade_date,
        gradeDate: entry.grade_date ?? null,
        initiator: formatUserName(userMap, entry.graded_by),
        comments: entry.grade_comments ?? null,
        actions: null,
    })

    const parseAcknowledgementFromHistory = (entry: any) => ({
        status: true,
        isAnnulled: false,
        title: intl.formatMessage({ id: 'assessment.acknowledged' }),
        tagline: entry.acknowledged_at,
        gradeDate: null,
        initiator: null,
        comments: null,
        actions: null,
    })

    const parseAnnulledEntry = (entry: any, userMap: Record<number, any>) => ({
        status: true,
        isAnnulled: true,
        title: intl.formatMessage({ id: 'assessment.annulled' }),
        tagline: entry.annulled_at,
        gradeDate: null,
        initiator: formatUserName(userMap, entry.annulled_by),
        comments: entry.annul_comment ?? null,
        actions: null,
    })

    const formatOldGrade = (grade: any): string | null => {
        if (grade === null || grade === undefined || grade === '') return null;
        if (grade === 'PASSED') return intl.formatMessage({ id: 'general.passed' });
        if (grade === 'FAILED') return intl.formatMessage({ id: 'general.failed' });
        return String(grade);
    };

    // Grade edits ("Labot vērtējumu") land in submission_data.edit_history -
    // without these rows an edit silently replaces the visible grade and the
    // overview shows no trace of the previous value (audit gap).
    const parseEditedEntry = (entry: any, userMap: Record<number, any>) => {
        const oldGrade = formatOldGrade(entry.old?.grade);

        return {
            status: true,
            isAnnulled: false,
            title:
                intl.formatMessage({ id: 'assessment.grade_edited' }) +
                (oldGrade
                    ? ` (${intl.formatMessage({ id: 'assessment.previous_grade' })}: ${oldGrade})`
                    : ''),
            tagline: entry.edited_at,
            gradeDate: entry.old?.grade_date ?? null,
            initiator: formatUserName(userMap, entry.edited_by),
            comments: entry.old?.grade_comments ?? null,
            actions: null,
        };
    };

    // Task17: one chronological row per submission_events entry.
    const parseEvent = (event: any) => {
        const data = event.eventData ?? {};

        switch (event.eventType) {
            case 'INITIATED':
                return {
                    status: true,
                    title: intl.formatMessage({ id: 'assessment.assessment_initiated' }),
                    tagline: event.happenedAt,
                    initiator: event.causedByName,
                    comments: null,
                    actions: null,
                };
            case 'INSTRUCTOR_COMMENT':
                return {
                    status: true,
                    title: event.causedByName ?? intl.formatMessage({ id: 'assessment.submit_comment' }),
                    tagline: event.happenedAt,
                    initiator: null,
                    comments: data.comment ?? null,
                    actions: null,
                };
            case 'GRADED':
                return {
                    status: true,
                    title: intl.formatMessage({ id: 'assessment.final_assessment' }),
                    tagline: event.happenedAt,
                    gradeDate: data.grade_date ?? null,
                    initiator: event.causedByName,
                    comments: data.comments ?? null,
                    actions: null,
                };
            case 'GRADE_EDITED': {
                const oldGrade = formatOldGrade(data.old?.grade);
                const fmtDate = (value: any) =>
                    value ? moment(value).format(config.defaultDateFormat) : '-';

                // Live events carry old+new - show what actually changed
                // (grade and/or grade date); backfilled ones have old only.
                const changes: string[] = [];
                if (data.new) {
                    if (String(data.new.grade ?? '') !== String(data.old?.grade ?? '')) {
                        changes.push(`${formatOldGrade(data.old?.grade) ?? '-'} → ${formatOldGrade(data.new.grade) ?? '-'}`);
                    }
                    if (fmtDate(data.new.grade_date) !== fmtDate(data.old?.grade_date)) {
                        changes.push(`${fmtDate(data.old?.grade_date)} → ${fmtDate(data.new.grade_date)}`);
                    }
                }

                const suffix = changes.length
                    ? ` (${changes.join(', ')})`
                    : oldGrade
                        ? ` (${intl.formatMessage({ id: 'assessment.previous_grade' })}: ${oldGrade})`
                        : '';

                return {
                    status: true,
                    title: intl.formatMessage({ id: 'assessment.grade_edited' }) + suffix,
                    tagline: event.happenedAt,
                    gradeDate: data.new?.grade_date ?? data.old?.grade_date ?? null,
                    initiator: event.causedByName,
                    // The comment the edit SET (live events carry old+new);
                    // backfilled events only know the old one.
                    comments: data.note ?? data.new?.grade_comments ?? data.old?.grade_comments ?? null,
                    actions: null,
                };
            }
            case 'ANNULLED':
                return {
                    status: true,
                    isAnnulled: true,
                    title: intl.formatMessage({ id: 'assessment.annulled' }),
                    tagline: event.happenedAt,
                    initiator: event.causedByName,
                    comments: data.annul_comment ?? null,
                    actions: null,
                };
            case 'ACKNOWLEDGED':
                return {
                    status: true,
                    title: intl.formatMessage({ id: 'assessment.acknowledged' }),
                    tagline: event.happenedAt,
                    initiator: event.causedByName,
                    comments: null,
                    actions: null,
                };
            case 'TASK_DELETED':
                return {
                    status: true,
                    isAnnulled: true,
                    title: intl.formatMessage({ id: 'grading.task_deleted' }),
                    tagline: event.happenedAt,
                    initiator: event.causedByName,
                    comments: null,
                    actions: null,
                };
            default:
                return {
                    status: true,
                    title: event.eventType,
                    tagline: event.happenedAt,
                    initiator: event.causedByName,
                    comments: null,
                    actions: null,
                };
        }
    };

    useEffect(() => {
        if(submission && activityFormData.attemptData) {
            const initiateData    = parseInitiateData(activityFormData.attemptData.initiateData)
            const instructorsData = activityFormData.attemptData.instructorsData.map((instructorData: any) => parseInstructorData(instructorData))
            const submitData      = parseSubmitData(activityFormData.attemptData.finalAssessment)

            const userMap = buildUserMap(activityFormData.instructors ?? []);
            const events: any[] = activityFormData.attemptData.events ?? [];

            let rows: any[];

            if (events.length > 0) {
                // Chronological feed from submission_events + pending
                // action rows (states are not events: waiting to initiate,
                // instructors yet to comment, grade pending).
                rows = events.map(parseEvent);

                if (!activityFormData.attemptData.initiateData?.isInitiated) {
                    rows.unshift(initiateData);
                }

                instructorsData
                    .filter((row: any) => !row.status)
                    .forEach((row: any) => rows.push(row));

                if (!activityFormData.attemptData.finalAssessment?.isSubmitted) {
                    rows.push(submitData);
                }
            } else {
                // Legacy glue from submission_data histories (not yet backfilled)
                const annulledHistory: any[] = submission?.submission_data?.annulled_history ?? [];
                const historyFeedRows: any[] = [];
                annulledHistory.forEach((entry: any) => {
                    historyFeedRows.push(parseGraduationFromHistory(entry, userMap));
                    if (entry.acknowledged_at) {
                        historyFeedRows.push(parseAcknowledgementFromHistory(entry));
                    }
                    historyFeedRows.push(parseAnnulledEntry(entry, userMap));
                });

                const editHistory: any[] = submission?.submission_data?.edit_history ?? [];
                editHistory.forEach((entry: any) => {
                    historyFeedRows.push(parseEditedEntry(entry, userMap));
                });

                rows = [initiateData, ...instructorsData, ...historyFeedRows, submitData];
            }

            const acknowledgement = activityFormData.attemptData.finalAssessment?.acknowledgement;
            if (acknowledgement?.required && !acknowledgement?.acknowledgedAt) {
                rows.push(parseAcknowledgementData(acknowledgement));
            } else if (events.length === 0 && acknowledgement?.required) {
                rows.push(parseAcknowledgementData(acknowledgement));
            }

            setOverviewRows(rows)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[activityFormData])

    const {appendData: initiateAssessment} = useQueryApiClient({
        request: {
            url: `/api/v1/courses/assessment-results/initiate/${submission.id}`,
            method: 'POST'
        },
        onSuccess: (): void => {
            message.success(intl.formatMessage({id: 'assessment.initiated_successfully'}))
            loadAssignment()
        },
        onFinally: (): void => setIsSubmitLoading(false)
    });

    const {appendData: submitInstructor} = useQueryApiClient({
        request: {
            url: `/api/v1/courses/assessment-results/submit-instructor/${submission.id}`,
            method: 'POST'
        },
        onSuccess: (): void => {
            message.success(intl.formatMessage({id: 'courses.evaluation_submitted_success'}))
            loadAssignment()
        },
        onFinally: (): void => {
            setIsSubmitLoading(false)
            setSelectedInstructor(null)
        }
    });

    const submitForm = async (values: any) => {
        setIsSubmitLoading(true)
        submitInstructor({
            instructorId: selectedInstructor.instructorUser.typeId,
            comments: values?.instructorComments
        })
    };

    const truncateWords = (text: string, maxWords: number): string => {
        const words = text.trim().split(/\s+/);
        if (words.length <= maxWords) return text;
        return words.slice(0, maxWords).join(' ') + '...';
    };

    const columns = [
        {
            title: intl.formatMessage({id: 'general.event'}),
            dataIndex: 'title',
            key: 'title',
        },
        {
            title: intl.formatMessage({id: 'general.grade_date'}),
            dataIndex: 'gradeDate',
            key: 'gradeDate',
            render: (gradeDate: string) => gradeDate ? moment(gradeDate).format(config.defaultDateFormat) : '-',
        },
        {
            title: intl.formatMessage({id: 'general.execution_date'}),
            dataIndex: 'tagline',
            key: 'tagline',
            render: (tagline: string) => tagline ? moment(tagline).format(config.defaultFullDateFormat) : '-',
        },
        {
            title: intl.formatMessage({id: 'general.comments'}),
            dataIndex: 'comments',
            key: 'comments',
            render: (comments: string) => comments ?? '-',
        }
    ];

    return (
        <div className='assessment-overview'>
            {selectedInstructor === null && overviewRows.map((item: any, key: number) => (
                <div
                    key={key}
                    className={`assessment-row flex flex-wrap flex-align-center w-100 ${item.isAnnulled ? 'is-annulled' : item.status ? 'is-submitted' : item.isPending ? 'is-pending' : ''}`}
                >
                    <div className='row-icon'>
                        <i className={item.isAnnulled ? 'fa-light fa-circle-xmark' : 'fa-light fa-circle-check'} />
                    </div>
                    <div className='row-title'>
                        <h4>{item.title}</h4>
                        <div className='flex gap-10'>
                            {item.tagline && moment(item.tagline).format(config.defaultFullDateFormat)}
                            {item.initiator && (
                                <span className='bold'>{item.initiator}</span>
                            )}
                        </div>
                    </div>
                    <div className='row-comments'>
                        {item.comments && <>
                            <h4><FormattedMessage id='general.comments' /></h4>
                            <div>{truncateWords(item.comments, 5)}</div>
                        </>}
                    </div>
                    <div className='row-actions'>
                        {item.actions && (
                            <Button
                                type='primary'
                                loading={isSubmitLoading}
                                onClick={item.actions.onClick}
                            >
                                {item.actions.message}
                            </Button>
                        )}
                    </div>
                </div>
                )
            )}
            {selectedInstructor !== null && (
                <Form className='assessment-instructor-comment-form' form={form} onFinish={submitForm}>
                    <TextArea
                        name='instructorComments'
                        label={intl.formatMessage({id: 'general.comments'})}
                        validation={{required: true}}
                        customLayout={{labelCol: 0, wrapperCol: 24}}
                        autoSize={{ minRows: 3 }}
                        showCount
                        maxLength={2000}
                    />
                    <div className='flex gap-10 justify-end' style={{paddingTop: 12}}>
                        <Button
                            type='primary'
                            htmlType='submit'
                            loading={isSubmitLoading}
                        >
                            <FormattedMessage id='assessment.submit_comment' />
                        </Button>
                        <Button
                            type='ghost'
                            htmlType='button'
                            onClick={() => setSelectedInstructor(null)}
                        >
                            <FormattedMessage id='general.cancel' />
                        </Button>
                    </div>
                </Form>
            )}
            {overviewRows.some((row: any) => row.comments) && (
                <Table
                    columns={columns}
                    pagination={false}
                    rowKey={(_, index) => String(index)}
                    dataSource={overviewRows.filter((row: any) => row.comments)}
                />
            )}
        </div >
    )
}
export default AssessmentOverview;